import { createContext } from 'react'
import type { PortalRole } from './roles'

/**
 * Objetos de contexto y sus tipos.
 *
 * Separados de los providers (`AuthContext.tsx`, `MeContext.tsx`) porque
 * `react-refresh/only-export-components` exige que un archivo con componentes
 * exporte solo componentes. Los hooks viven en `useAuth.ts` y `useMe.ts`, que
 * es el patrón que ya usaba el portal.
 */

export interface AuthUser {
  email: string
  /** «Ruben Quispe», del ID token (`auth/nombre.ts`); '' si todavía no lo trae. */
  name: string
  groups: string[]
  exp: number
}

export interface AuthContextValue {
  user: AuthUser | null
  /** Rol efectivo. null = logeado pero sin ningún grupo reconocido. */
  role: PortalRole | null
  isAdmin: boolean
  isSupervisor: boolean
  isAsesor: boolean
  /** Puede atender conversaciones (el asesor, sobre las suyas). */
  canOperate: boolean
  /** Puede ver la cola global, otros asesores y métricas del equipo. */
  canSupervise: boolean
  /** Está en el grupo Asesor de Cognito: el round-robin le asigna casos. */
  atiende: boolean
  isAuthenticated: boolean
  /** Logeado pero sin Asesor/Supervisor/Admin → sin acceso al portal. */
  hasNoRole: boolean
  isLoading: boolean
  login: () => void
  logout: () => void
}

export const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Estado operativo del usuario: disponibilidad para el round-robin y su lugar
 * en la rotación.
 *
 * Vive aparte de AuthContext a propósito. AuthContext deriva todo del JWT y no
 * hace red; esto consulta `GET /me` y cambia varias veces al día cuando el
 * asesor toca el pill del header. Mezclarlos haría que cada cambio de
 * disponibilidad re-renderice todo lo que depende de la sesión.
 */
export interface MeState {
  available: boolean
  /** 1 = el próximo en recibir. null si está en pausa o no es asesor. */
  rotationPosition: number | null
  rotationSize: number | null
  loading: boolean
  /** Mensaje de error del último intento de cambiar la disponibilidad. */
  error: string | null
  setAvailable: (value: boolean) => Promise<void>
  refresh: () => Promise<void>
}

export const MeContext = createContext<MeState | null>(null)
